import React, { useEffect, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardContent,
  CardActions,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Loading from "../common/Loading";
import AppSnackbar from "../../common/AppSnackbar";
import { useVacationsAPI } from "../../service/useVacationsAPI";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatDate,
  getDateStringToAvoidTimezone,
} from "../../common/DateUtil";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import nl from "date-fns/locale/nl";
import { addDays, parseISO } from "date-fns";

// Stacked label/value helper (great on mobile)
const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
    <Typography variant="caption" sx={{ opacity: 0.8 }}>
      {label}
    </Typography>
    <Typography>{value ?? "-"}</Typography>
  </Box>
);

const Vacations = () => {
  const { t } = useTranslation();
  const { getVacations, createVacation, deleteVacation } = useVacationsAPI();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [vacations, setVacations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isEditing, setIsEditing] = useState(null); // index or null
  const [yearVacations, setYearVacations] = useState(new Date().getFullYear());

  function fetchVacations(year) {
    setIsLoading(true);
    getVacations(year)
      .then((data) => setVacations(data))
      .catch(() => {
        setSnackbarMessage(t("error_fetching_vacations"));
        setOpenSnackbar(true);
      })
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if (yearVacations) fetchVacations(yearVacations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearVacations]);

  const handleAddVacation = () => {
    const newVacation = { fromDate: "", toDate: "" };
    setVacations((prev) => [newVacation, ...prev]);
    setIsEditing(0);
  };

  const handleEdit = (index, key, value) => {
    const updated = [...vacations];
    updated[index][key] = value ? getDateStringToAvoidTimezone(value) : "";
    setVacations(updated);
  };

  const handleSave = (index) => {
    const vacation = vacations[index];
    if (!vacation?.fromDate || !vacation?.toDate) return;
    createVacation(vacation)
      .then(() => fetchVacations(yearVacations))
      .catch(() => {
        setSnackbarMessage(t("error_creating_vacations"));
        setOpenSnackbar(true);
      })
      .finally(() => setIsEditing(null));
  };

  const handleDelete = (index) => {
    const vacation = vacations[index];
    if (!vacation) return;
    deleteVacation(vacation)
      .then(() => fetchVacations(yearVacations))
      .catch(() => {
        setSnackbarMessage(t("error_deleting_vacations"));
        setOpenSnackbar(true);
      });
  };

  const handleCancel = () => {
    setIsEditing(null);
    fetchVacations(yearVacations);
  };

  const parseDate = (dateString) => (dateString ? new Date(dateString) : null);

  const yearOptions = Array.from(
    { length: 4 },
    (_, i) => new Date().getFullYear() - 1 + i,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
      {isLoading ? (
        <Loading />
      ) : (
        <Grid
          container
          justifyContent="center"
          sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}
        >
          <Grid item xs={12} sm={12} sx={{ p: 2, m: 2 }}>
            <Grid item xs={12}>
              <Typography variant={isXs ? "h6" : "h5"} sx={{ mb: 1 }}>
                {t("vacations")}
              </Typography>
            </Grid>

            {/* Controls */}
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Select
                variant={"outlined"}
                size={isXs ? "small" : "medium"}
                value={yearVacations}
                onChange={(e) => setYearVacations(e.target.value)}
                sx={{ minWidth: 120 }}
              >
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
              <Button
                onClick={handleAddVacation}
                variant="contained"
                startIcon={<AddIcon />}
                data-testid="add-vacation-button"
              >
                {t("add_new_vacations")}
              </Button>
            </Grid>

            {/* Mobile: cards; Desktop: table */}
            {isXs ? (
              <Grid item xs={12}>
                <Grid container spacing={1}>
                  {vacations.map((vacation, index) => {
                    const editing = isEditing === index;
                    return (
                      <Grid item xs={12} key={`${vacation.id || index}`}>
                        <Card variant="outlined">
                          <CardContent sx={{ display: "grid", gap: 1 }}>
                            {editing ? (
                              <>
                                <DatePicker
                                  label={t("start_date")}
                                  value={parseDate(vacation.fromDate)}
                                  onChange={(v) =>
                                    handleEdit(index, "fromDate", v)
                                  }
                                  slotProps={{ textField: { fullWidth: true } }}
                                />
                                <DatePicker
                                  label={t("end_date")}
                                  value={parseDate(vacation.toDate)}
                                  minDate={
                                    vacation.fromDate
                                      ? addDays(parseISO(vacation.fromDate), 1)
                                      : undefined
                                  }
                                  onChange={(v) =>
                                    handleEdit(index, "toDate", v)
                                  }
                                  slotProps={{ textField: { fullWidth: true } }}
                                />
                              </>
                            ) : (
                              <>
                                <InfoRow
                                  label={t("start_date")}
                                  value={formatDate(vacation.fromDate)}
                                />
                                <InfoRow
                                  label={t("end_date")}
                                  value={formatDate(vacation.toDate)}
                                />
                              </>
                            )}
                          </CardContent>
                          <CardActions sx={{ justifyContent: "space-between" }}>
                            {editing ? (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Button
                                  onClick={() => handleSave(index)}
                                  startIcon={<SaveIcon />}
                                  variant="contained"
                                  data-testid={`save-vacation-button-card-${index}`}
                                >
                                  {t("save")}
                                </Button>
                                <Button
                                  onClick={() => handleCancel()}
                                  startIcon={<CloseIcon />}
                                  data-testid={`cancel-vacation-button-card-${index}`}
                                >
                                  {t("cancel")}
                                </Button>
                              </Box>
                            ) : (
                              <IconButton
                                onClick={() => handleDelete(index)}
                                color="error"
                                data-testid={`delete-vacation-button-card-${index}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <TableContainer component={Paper}>
                  <Table size="small" aria-label="vacations table">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t("start_date")}</TableCell>
                        <TableCell>{t("end_date")}</TableCell>
                        <TableCell>{t("actions")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {vacations.map((vacation, index) => (
                        <TableRow key={vacation.id || index}>
                          <TableCell>
                            {isEditing === index ? (
                              <DatePicker
                                label={t("start_date")}
                                value={parseDate(vacation.fromDate)}
                                onChange={(v) =>
                                  handleEdit(index, "fromDate", v)
                                }
                                slotProps={{ textField: { fullWidth: true } }}
                              />
                            ) : (
                              formatDate(vacation.fromDate)
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing === index ? (
                              <DatePicker
                                label={t("end_date")}
                                value={parseDate(vacation.toDate)}
                                minDate={
                                  vacation.fromDate
                                    ? addDays(parseISO(vacation.fromDate), 1)
                                    : undefined
                                }
                                onChange={(v) => handleEdit(index, "toDate", v)}
                                slotProps={{ textField: { fullWidth: true } }}
                              />
                            ) : (
                              formatDate(vacation.toDate)
                            )}
                          </TableCell>
                          <TableCell>
                            {isEditing === index ? (
                              <>
                                <Button
                                  onClick={() => handleSave(index)}
                                  variant="contained"
                                  color="primary"
                                  startIcon={<SaveIcon />}
                                  sx={{ mr: 1 }}
                                  data-testid={`save-vacation-button-row-${index}`}
                                >
                                  {t("save")}
                                </Button>
                                <Button
                                  onClick={() => handleCancel(index)}
                                  startIcon={<CloseIcon />}
                                  data-testid={`cancel-vacation-button-row-${index}`}
                                >
                                  {t("cancel")}
                                </Button>
                              </>
                            ) : (
                              <IconButton
                                onClick={() => handleDelete(index)}
                                color="error"
                                data-testid={`delete-vacation-button-row-${index}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            )}

            <AppSnackbar
              openSnackbar={openSnackbar}
              snackbarMessage={snackbarMessage}
              setOpenSnackbar={setOpenSnackbar}
            />
          </Grid>
        </Grid>
      )}
    </LocalizationProvider>
  );
};

export default withAuthenticationRequired(Vacations, {
  onRedirecting: () => <Loading />,
  returnTo: "/beheer/login",
});
